import { worldLayer } from '@backoffice-frontend/common'
import { useEffect, useRef } from 'react'
import { safeRemoveObject } from '../utils/safeRemoveObject'
import { screenToLocation } from '../utils/screenToLocation'
import type { Location } from './useHereLocation'
import { useLineDash } from './useLineDash'

const parseGeoJson = (
  geoJson: string,
): { outerRings: H.geo.IPoint[][]; innerRings: H.geo.IPoint[][] } => {
  const parsed = JSON.parse(geoJson)
  const outerRings: H.geo.IPoint[][] = []
  const innerRings: H.geo.IPoint[][] = []

  const convertCoords = (coords: number[][]): H.geo.IPoint[] =>
    coords.map(([lng, lat]) => ({ lat, lng }))

  if (parsed.type === 'Polygon') {
    const coords = parsed.coordinates as number[][][]
    outerRings.push(convertCoords(coords[0]))
    for (let i = 1; i < coords.length; i++) {
      innerRings.push(convertCoords(coords[i]))
    }
  } else if (parsed.type === 'MultiPolygon') {
    const polygons = parsed.coordinates as number[][][][]
    for (const coords of polygons) {
      outerRings.push(convertCoords(coords[0]))
      for (let i = 1; i < coords.length; i++) {
        innerRings.push(convertCoords(coords[i]))
      }
    }
  }

  return { outerRings, innerRings }
}

const pointsToLineString = (points: H.geo.IPoint[]): H.geo.LineString => {
  const line = new H.geo.LineString()
  for (const point of points) {
    line.pushPoint(point)
  }
  return line
}

/**
 * usePolygon is a low-level hook that puts a polygon/area on the map.
 * @param map The HERE map instance.
 * @param points The points that make up the outline of the polygon.
 * @param geoJson A GeoJSON string (Polygon or MultiPolygon) as an alternative to points.
 * @param isCutout Let the inside of the polygon be transparent and the outside be filled.
 * @param strokeColor The color of the line around the polygon.
 * @param fillColor The color that fills the inside of the polygon.
 * @param lineDash The dash pattern of the line around the polygon.
 * @param lineWidth The width of the line around the polygon.
 * @param innerRings Additional inner rings for cutout areas within the polygon.
 * @param onClick Callback when the polygon is clicked, receives the geo location of the click. When provided, the cursor automatically changes to 'pointer' on hover.
 */
export const usePolygon = ({
  map,
  points,
  geoJson,
  isCutout = false,
  strokeColor,
  fillColor,
  lineDash: lineDashProp = [1],
  lineWidth = 1,
  innerRings,
  onClick,
}: {
  map?: H.Map
  points?: H.geo.IPoint[]
  geoJson?: string
  isCutout?: boolean
  strokeColor: string
  fillColor: string
  lineDash?: number[]
  lineWidth?: number
  innerRings?: H.geo.IPoint[][]
  onClick?: (location: Location) => void
}) => {
  const polygonRef = useRef<H.map.Polygon | undefined>(undefined)
  const lineDash = useLineDash(lineDashProp)
  const onClickRef = useRef(onClick)
  onClickRef.current = onClick
  const isClickable = !!onClick

  useEffect(() => {
    if (!map) return
    if (!points && !geoJson) return

    let geometry: H.geo.Polygon | H.geo.MultiPolygon

    if (geoJson) {
      const { outerRings, innerRings: geoJsonInnerRings } =
        parseGeoJson(geoJson)
      const allInnerRings = [...geoJsonInnerRings, ...(innerRings ?? [])]

      if (outerRings.length === 1) {
        const outerLine = pointsToLineString(outerRings[0])
        const innerLineStrings = allInnerRings.map(pointsToLineString)

        // always wrap in H.geo.Polygon: HERE only hit-tests the filled surface
        // of closed geometries, a bare LineString is clickable on its stroke only
        geometry = new H.geo.Polygon(outerLine, innerLineStrings)
      } else {
        const polygons = outerRings.map(outerRing => {
          const outerLine = pointsToLineString(outerRing)
          return new H.geo.Polygon(outerLine)
        })
        geometry = new H.geo.MultiPolygon(polygons)
      }
    } else if (points) {
      const polygonLine = pointsToLineString(points)
      const allInnerRings = innerRings ?? []

      if (isCutout) {
        const worldPolygonLine = pointsToLineString(worldLayer)

        const innerLineStrings = [
          polygonLine,
          ...allInnerRings.map(pointsToLineString),
        ]

        geometry = new H.geo.Polygon(worldPolygonLine, innerLineStrings)
      } else {
        const innerLineStrings = allInnerRings.map(pointsToLineString)
        geometry = new H.geo.Polygon(polygonLine, innerLineStrings)
      }
    } else {
      return
    }

    const polygon = new H.map.Polygon(geometry, {
      style: {
        strokeColor,
        fillColor,
        lineWidth,
        lineCap: 'round',
        lineDash,
      },
      data: {},
    })
    map.addObject(polygon)
    polygonRef.current = polygon

    if (isClickable) {
      const onTap = (evt: H.mapevents.Event) => {
        const location = screenToLocation(map, evt)
        if (location) onClickRef.current?.(location)
      }
      const onPointerEnter = () => {
        map.getViewPort().element.style.cursor = 'pointer'
      }
      const onPointerLeave = () => {
        map.getViewPort().element.style.cursor = 'auto'
      }
      polygon.addEventListener('tap', onTap)
      polygon.addEventListener('pointerenter', onPointerEnter)
      polygon.addEventListener('pointerleave', onPointerLeave)
    }

    return () => {
      safeRemoveObject(map, polygon)
      polygonRef.current = undefined
    }
  }, [map, points, geoJson, isCutout, innerRings, isClickable])

  // Style-only (avoids destroying/recreating the polygon)
  useEffect(() => {
    polygonRef.current?.setStyle({
      strokeColor,
      fillColor,
      lineWidth,
      lineCap: 'round',
      lineDash,
    })
  }, [strokeColor, fillColor, lineDash, lineWidth])
}
